import type { SourceAttributionReferenceType } from "@augloop-types/sydney";
import type { PartsData, PresentationInfo } from "./DocumentMetadataTypes";
import type { MailboxInformation } from "./MailboxInformation";
import type { OAIReferences } from "./ReferencesSchema";
import type { SensitivityLabel } from "./SensitivityLabelType";

export enum QueryAnnotationType {
  File = "File",
  LocalFile = "LocalFile",
  FileUrl = "FileUrl",
  Folder = "Folder",
  People = "People",
  Event = "Event",
  Chat = "Chat",
  Channel = "Channel",
  Message = "Message",
  AutomaticReply = "AutomaticReply",
  Topic = "Topic",
  External = "External",
  Image = "Image",
  GenericList = "GenericList",
  Site = "Site",
  Page = "Page",
  Workspace = "Workspace",
  SitePage = "SitePage",
  Table = "Table",
  DesignEditor = "DesignEditor",
  TeamsMeeting = "TeamsMeeting",
  EdgeTab = "EdgeTab",
  OutlookCanvas = "OutlookCanvas",
  M365ChatCanvas = "M365ChatCanvas",
  ImageFile = "ImageFile",
  App = "App",
  PowerApps = "PowerApps",
  CopilotSearch = "CopilotSearch",
  EmailConversation = "EmailConversation",
  SchedulingEventIntent = "SchedulingEventIntent",
  ClassicAttachment = "ClassicAttachment",
  Forms = "Forms",
  VivaGlint = "VivaGlint",
  PlannerPlan = "PlannerPlan",
  CopilotStudio = "CopilotStudio",
  FinanceAndOperations = "FinanceAndOperations",
  VivaEngage = "VivaEngage",
  OneDrive = "OneDrive",
  Sales = "Sales",
  FinanceAgent = "FinanceAgent",
  SharePoint = "SharePoint",
  BusinessCentral = "BusinessCentral",
  Fabric = "Fabric",
  M365AdminCopilot = "M365AdminCopilot",
  SharePointAdminCenter = "SharePointAdminCenter",
  CopilotBoards = "CopilotBoards",

  D365ContactCenterEdge = "D365ContactCenterEdge",

  CollaborationProtection = "CollaborationProtection",
  TeamsAdminCenter = "TeamsAdminCenter",
}

export enum MessageAnnotationSource {
  // This is the default value. It indicates a User attached or mentioned the entity using CIQ or upload option.
  UserAnnotated = "UserAnnotated",
  // Indicates the entity is implicitly provided by the client. In the case of loop pages, the opened loop page provides additional implicit context in the client.
  ClientAnnotated = "ClientAnnotated",
  // Indicates that an entity was added by Sydney/extensions.
  SystemAnnotated = "SystemAnnotated",
}

// https://msasg.visualstudio.com/WIT%20Engineering/_git/TuringAtWork?path=/services/TuringBot/Microsoft.TuringBot.Common/Contract/FilesAndLinksContextMetadata.cs&version=GBmaster&line=562&lineEnd=566&lineStartColumn=1&lineEndColumn=30&lineStyle=plain&_a=contents
export enum FileStoreType {
  ODSP = "ODSP",
  AugLoop = "AugLoop",
}

export interface CanvasSelectedContent {
  text: string;
  pictures: Array<string>;
}
interface SelectedContentContext {
  // the selected text in the page
  readonly selectedContent?: string;
  // the start offset of the selected text in the page
  readonly startOffset?: number;
}

interface SourceAttribution {
  readonly displayName?: string;
  readonly seeMoreUrl?: string;
}

interface QueryAnnotationBase {
  readonly id: string;
  readonly text?: string;
  readonly url?: string;
  readonly context?: string;
  readonly messageAnnotationAppContext?: string;
  readonly messageAnnotationMetadata?: MessageAnnotationMetaData;
  readonly messageAnnotationSource?: MessageAnnotationSource;
}

export type QueryAnnotation = QueryAnnotationBase & {
  readonly queryAnnotationType: QueryAnnotationType;
};

export type MessageAnnotation = QueryAnnotationBase & {
  readonly messageAnnotationType: QueryAnnotationType;
};

export type MessageAnnotationMetaData =
  | MessageAnnotationMetaDataBase
  | ChatAnnotationMetaData
  | ChannelAnnotationMetaData
  | FileAnnotationMetaData
  | PageAnnotationMetaData
  | LoopWorkspaceAnnotationMetaData
  | TableAnnotationMetaData
  | EventAnnotationMetaData
  | ExternalAnnotationMetaData
  | MessageAnnotationMetaDataTeamsMeeting
  | MultiImageAnnotationMetaData
  | ImageFileAnnotationMetaData
  | AppAnnotationMetaData
  | DesignEditorAnnotationMetaData
  | EdgeTabAnnotationMetaData
  | OutlookCanvasContextMetadata
  | M365ChatCanvasContextMetadata
  | EmailMessageContextMetadata
  | EventMessageAnnotationMetadata
  | SearchAnnotationMetaData
  | PowerAppsAnnotationMetaData
  | FormsAnnotationMetaData
  | VivaGlintAnnotationMetaData
  | CopilotStudioAnnotationMetaData
  | FinanceAndOperationsAnnotationMetaData
  | VivaEngageAnnotationMetaData
  | OneDriveAnnotationMetaData
  | BusinessCentralAnnotationMetaData
  | FabricAnnotationMetaData
  | M365AdminCopilotAnnotationMetaData
  | CopilotBoardsAnnotationMetaData
  | D365ContactCenterEdgeAnnotationMetaData
  | CollaborationProtectionAnnotationMetaData
  | TeamsAdminCenterAnnotationMetaData
  | SchedulingEventIntentAnnotationMetaData
  | PlannerAnnotationMetaData
  | OnePartNotebookAnnotationMetaData
  | SharePointAdminCenterAnnotationMetaData;

export type MultiImageAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly annotationType?: string;
  readonly fileType?: string;
  readonly fileName?: string;
};

export type ExternalAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly contentSource?: string;
};

export type MessageAnnotationMetaDataBase = {
  readonly "@type"?: QueryAnnotationType;
  readonly fileInfo?: MessageAnnotationMetaDataFileInfo;
};

export type OnePartNotebookAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    readonly partId?: string;
  };

export type SearchAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly searchType?: string;
  readonly appName?: string;
  readonly entityTypes?: string[];
  readonly sourceContext?: string;
};

export type ChatAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly startTime?: number;
};

export type ChannelAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly channelId: string;
  readonly teamId: string;
  readonly groupId?: string;
  readonly replyChainId?: string;
  readonly tenantId?: string;
  readonly threadId?: string;
};

export type PlannerAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly selectedContentContext?: PlannerSelectedContentContext;
};

export type PlannerSelectedContentContext = {
  readonly taskIds?: string[];
  readonly goalIds?: string[];
  readonly reportIds?: string[];
  readonly bucketIds?: string[];
};
export enum FileContentKind {
  Unknown = "Unknown",
  DesignRasterImage = "DesignRasterImage",
  Spreadsheet = "Spreadsheet",
  Document = "Document",
  Presentation = "Presentation",
  MaskImage = "MaskImage",
}

export type FileAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly id?: string;
  readonly fileName?: string;
  readonly fileType?: string;
  readonly fileContent?: string;
  readonly sensitivityLabel?: SensitivityLabel;
  readonly context?: string;
  readonly contentKind?: FileContentKind;
  readonly clientFileMetadata?: DocumentMetadata;
  readonly documentMetadata?: DocumentMetadata;
  readonly selectedContentContext?: SelectedContentContext;
  readonly unifiedSelectedContentContext?: UnifiedSelectedContentContext;
};

export type ExternalAnnotationMetadata = MessageAnnotationMetaDataBase & {
  readonly contentSource?: string;
};

export type PageAnnotationMetaData = FileAnnotationMetaData & {
  readonly isOpen?: boolean;
  readonly inViewport?: boolean;
  readonly selectedContentContext?: SelectedContentContext;
  readonly sourceAttributions?: SourceAttribution[];
  readonly references?: OAIReferences;
};

export type LoopWorkspaceAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly spoId?: string;
  readonly workspaceId?: string;
  readonly siteId?: string;
  readonly tenantId?: string;
  readonly scopeToWorkspace?: boolean;
  readonly skipFileAnnotationUnpacking?: boolean;
};

export type TableAnnotationMetaData = MessageAnnotationMetaDataBase & {
  "@type": QueryAnnotationType.Table;
  readonly tableId?: string;
  readonly tableSelection?: {
    selections: (
      | {
          type: "Column";
          index: number;
          name: string;
        }
      | {
          type: "Range";
          startRowIndex: number;
          startColumnIndex: number;
          endRowIndex: number;
          endColumnIndex: number;
        }
      | {
          type: "Row";
          index: number;
        }
      | {
          type: "Table";
        }
    )[];
  };
};

type ConfirmationAnnotationMetaData = {
  readonly skipConfirmation: boolean;
};

export type EventAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly isSeries?: boolean;
};

export type SchedulingEventIntentAnnotationMetaData =
  MessageAnnotationMetaDataBase & ConfirmationAnnotationMetaData;

export type ImageFileAnnotationMetaData = FileAnnotationMetaData & {
  readonly imageReferenceUrl?: string;
  readonly fileDescription?: string;
};
export type AppAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly appId?: string;
  readonly sharePointSettings?: {
    readonly siteName?: string;
    readonly sensitivityLabel?: string;
    readonly additionalOwners?: string[];
  };
};

export type MessageAnnotationMetaDataFileInfo = {
  readonly fileContainerUrl?: string;
  readonly fileUrl?: string;
  readonly fileStoreType?: FileStoreType;

  /** @deprecated use fileUrl instead */
  readonly FileUrl?: string;
};

export type DesignEditorAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly isOpen?: boolean;
  readonly designFileId?: string;
  readonly pageId?: string;
  readonly designFileCacheInfoUrl?: string;
  readonly isBusy?: boolean;
};

export type MessageAnnotationMetaDataTeamsMeeting =
  MessageAnnotationMetaDataBase & {
    readonly callId?: string;
    readonly canvasType?: SessionType;
    readonly channelMessageId?: string;
    readonly groupId?: string;
    readonly isTemporaryStorageForMeetingTranscript?: boolean;
    readonly organizerId?: string;
    readonly threadId?: string;
    readonly vroomId?: string;
    readonly iCalUID?: string;
    readonly participantId?: string;
  };

export type EdgeTabAnnotationMetaData = MessageAnnotationMetaDataBase & {
  readonly isActive?: boolean;
  readonly pageType?: string;
  readonly lastAccessedTime?: string;

  // M365 link source info, consistent with m365LinkSourceInfo in EdgeTabInfo in scc-types.
  // Sydney will use this field to query for M365 link related context.
  readonly m365LinkSourceInfo?: {
    // The id of the M365 link source, if the source is outlook, it is email id.
    readonly id?: string;
    // The text of the M365 link source, e.g., subject of the email.
    readonly text?: string;
    // The source of the M365 link, comes from the app, e.g., "outlook", "teams", etc.
    readonly source?: string;
    // The annotation type of the M365 link, e.g., "Message", "EmailConversation", etc.
    readonly annotationType?: QueryAnnotationType;
  };
};

export enum CanvasModule {
  Mail = "Mail",
  Calendar = "Calendar",
}

export enum CanvasSourceScenario {
  Default = "Default",
  ImmersiveSearch = "ImmersiveSearch",
  ExplainThis = "ExplainThis",
}

export enum ListViewSelection {
  NoItemSelected = "NoItemSelected",
  SingleItemSelected = "SingleItemSelected",
  MultipleItemsSelected = "MultipleItemsSelected",
}

export interface CalendarRestriction {
  readonly calendarId?: string;
}

export type OutlookCanvasContextMetadata = MessageAnnotationMetaDataBase & {
  readonly appName?: string;
  readonly module?: CanvasModule;
  readonly sourceScenario?: CanvasSourceScenario;
  readonly listViewSelection?: ListViewSelection;
  readonly mailboxRestriction?: MailboxInformation[];
  readonly calendarRestriction?: CalendarRestriction[];
};

export type M365ChatCanvasContextMetadata = MessageAnnotationMetaDataBase & {
  readonly appName?: string;
  readonly mailboxRestriction?: MailboxInformation[];
  readonly calendarRestriction?: CalendarRestriction[];
};

export enum PowerAppsType {
  ModelApp = "ModelApp",
  CanvasApp = "CanvasApp",
  CodeApp = "CodeApp",
}

export type PowerAppsTelemetryContext = {
  clientSessionId?: string;
  clientRequestId?: string;
};

export type PowerAppsSelectedRecord = {
  "@type": "Record";
  properties: Record<string, unknown>;
};

export enum PowerAppsDataSourceType {
  Dataverse = "Dataverse",
  SharePointList = "SharePointList",
  Excel = "Excel",
  SQL = "SQL",
}

export type PowerAppsAnnotationMetaData = MessageAnnotationMetaDataBase & {
  schemaVersion?: string;
  appType?: PowerAppsType;
  appId?: string;
  appModule?: string;
  orgId?: string;
  geo?: string;
  entity?: string;
  filterXML?: string;
  filterId?: string;
  selectedRecords?: {
    selectedContents: PowerAppsSelectedRecord[];
  };
  extendedContext?: Record<string, unknown>;
  telemetryContext?: PowerAppsTelemetryContext;
  powerAppsMcpUrl?: string | null;
};

export type CollaborationProtectionAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    // Add specific properties or methods for CollaborationProtectionAnnotationMetaData here
  };

export type TeamsAdminCenterAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    // Add specific properties or methods for TeamsAdminCenterAnnotationMetaData here
  };

export type D365ContactCenterEdgeAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    // Add specific properties or methods for D365ContactCenterEdgeAnnotationMetaData here
  };

export type SharePointAdminCenterAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    // Add specific properties or methods for SharePointAdminCenterAnnotationMetaData here
  };

export type FabricAnnotationMetaData = MessageAnnotationMetaDataBase & {
  // Add specific properties or methods for FabricAnnotationMetaData here
};

export type OneDriveAnnotationMetaData = MessageAnnotationMetaDataBase & {
  // Add specific properties or methods for OneDriveAnnotationMetaData here
};

export type CopilotBoardsAnnotationMetaData = MessageAnnotationMetaDataBase & {
  // Add specific properties or methods for CopilotBoardsAnnotationMetaData here
};

export type FinanceAndOperationsTelemetryContext = {
  clientSessionId?: string;
  clientRequestId?: string;
};

export type FinanceAndOperationsUserPreferences = {
  // Comes from the $dyn object. Example: "en-us".
  language?: string;
  // Comes from the $dyn object. Example: "User|UST|0|0| ".
  timeZone?: string;
};

export type FinanceAndOperationsFormContext = {
  formLogicalName?: string;
  formCaption?: string;
  formHelpText?: string | null;
};

export type FinanceAndOperationsDataSource = {
  activeDataSourceName?: string | null;
  activeRecordData?: string | Record<string, unknown>;
};

export type M365AdminCopilotAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    // Add specific properties or methods for M365AdminCopilotAnnotationMetaData here
  };

export type FinanceAndOperationsAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    schemaVersion?: string;
    financeAndOperationsUrl?: string;
    currentLegalEntity?: string;
    menuItem?: string;
    userPreferences?: FinanceAndOperationsUserPreferences;
    formContext?: FinanceAndOperationsFormContext;
    dataSource?: FinanceAndOperationsDataSource;
    extendedContext?: Record<string, unknown>;
    telemetryContext?: FinanceAndOperationsTelemetryContext;
  };

export type BusinessCentralAnnotationMetaData =
  MessageAnnotationMetaDataBase & {
    appContext?: Record<string, string | Record<string, unknown>>;
    turnContext?: Record<string, string | Record<string, unknown>>;
  };

export type VivaEngageAnnotationMetaData = MessageAnnotationMetaDataBase & {
  threadId?: string;
  groupId?: string;
  communityId?: string;
  userId?: string;
  campaignId?: string;
  draftContent?: {
    "my post": string;
    "my selection"?: string;
  };
};

export type CopilotStudioAnnotationMetaData = MessageAnnotationMetaDataBase & {
  // TODO: Unknown properties for CopilotStudioAnnotationMetaData
  copilotStudioUrl?: string;
  isAgentBuilder?: boolean;
};

export type VivaGlintAnnotationMetaData = MessageAnnotationMetaDataBase & {
  copilotSubmitRequestView?: string;
};

export type FormsAnnotationMetaData = MessageAnnotationMetaDataBase & {
  formUrl?: string;
};

export enum SelectedContentContextType {
  Text = "Text",
  Sheet = "Sheet",
}

export type TextSelectionMetadata = SelectedContentContext & {
  readonly "@type": SelectedContentContextType.Text;
};

export type SheetSelectionMetadata = {
  readonly "@type": SelectedContentContextType.Sheet;
  name: string;
};

/**
 * SelectedContentMetadata is an union type representing various types of selected
 * content that can be used in UnifiedSelectedContentContext. There are more types
 * available and they can be added here as needed.
 */
export type SelectedContentMetadata =
  | TextSelectionMetadata
  | TableAnnotationMetaData
  | SheetSelectionMetadata;

interface UnifiedSelectedContentContext {
  readonly selectedContents?: SelectedContentMetadata[];
}

export interface Recipient {
  readonly name?: string;
  readonly emailAddress?: string;
}
export type EmailBodyFormat = "Html" | "Markdown" | "Text";

export type EmailMessageContextMetadata = MessageAnnotationMetaDataBase & {
  readonly mailboxInformation?: MailboxInformation;
  readonly selectedContentContext?: UnifiedSelectedContentContext;
  readonly precedingText?: string;
  readonly followingText?: string;
  readonly subject?: string;
  readonly toRecipients?: Recipient[];
  readonly ccRecipients?: Recipient[];
  readonly bccRecipients?: Recipient[];
  readonly isDraft?: boolean;
  readonly editorId?: string;
  readonly hasSignature?: boolean;
  readonly body?: string;
  readonly bodyFormat?: EmailBodyFormat;
  readonly msipLabelGuid?: string;
};

export type EventMessageAnnotationMetadata = MessageAnnotationMetaDataBase & {
  readonly mailboxInformation?: MailboxInformation;
  readonly isSeries?: boolean;
  readonly isDraft?: boolean;
  readonly subject?: string;
  readonly durationInMinutes?: number;
};

/**
 * Document State interface for passing document properties that describe the current state of a document.
 */
export interface DocumentState {
  isDocDirty: boolean;
  isCreateNewFromBlank: boolean;
}

/**
 * DocumentMetadata represents the internal structure and format-specific composition of a document.
 * For presentations, it includes slide information with IDs and indices for navigation and manipulation.
 * For all document types, it contains detailed information about the document's internal parts
 * (typically XML components in Office formats), including compression details (method, CRC32),
 * size information (compressed/uncompressed), and part naming for accessing specific document components.
 * This data is extracted from within the document package itself (e.g., from .pptx).
 * Sydney expects this field name to be `clientFileMetadata` in the message annotation metadata.
 */
export type DocumentMetadata = {
  sourceAttributionReferenceType: SourceAttributionReferenceType; // type deserializer
  presentationInfo?: PresentationInfo; // presentation specific information
  partData?: PartsData[]; // part specific information
  documentState?: DocumentState; // document state information
  pptMessageId?: string; // PPT-specific Message Id - to be able to identify and correlate with the request that generated this metadata
};

enum SessionType {
  Private = "Private",
  Shared = "Shared",
  Recap = "Recap",
  RecapCall = "RecapCall",
  PrivateViewCall = "PrivateViewCall",
  Chat = "Chat",
  Compose = "Compose",
  PrivateViewDuringMeeting = "PrivateViewDuringMeeting",
}
export type QueryAnnotationFile = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.File;
};

export type QueryAnnotationFolder = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Folder;
};

export type QueryAnnotationGenericList = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.GenericList;
};

export type QueryAnnotationLocalFile = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.LocalFile;
};

export type QueryAnnotationFileUrl = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.FileUrl;
};

export type QueryAnnotationPeople = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.People;
};

export type QueryAnnotationEvent = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Event;
};

export type QueryAnnotationChat = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Chat;
};

export type QueryAnnotationChannel = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Channel;
};

export type QueryAnnotationMessage = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Message;
};

export type QueryAnnotationAutomaticReply = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.AutomaticReply;
};

export type QueryAnnotationTopic = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Topic;
};

export type QueryAnnotationExternal = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.External;
};

export type QueryAnnotationImage = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Image;
};

export type QueryAnnotationSite = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Site;
};

export type QueryAnnotationSitePage = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.SitePage;
};

export type QueryAnnotationPage = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Page;
};

export type QueryAnnotationLoopWorkspace = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Workspace;
};

export type QueryAnnotationTable = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.Table;
};

export type QueryAnnotationDesignEditor = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.DesignEditor;
};

export type QueryAnnotationTeamsMeeting = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.TeamsMeeting;
};

export type QueryAnnotationEdgeTab = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.EdgeTab;
};

export type QueryAnnotationOutlookCanvas = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.OutlookCanvas;
};

export type QueryAnnotationM365ChatCanvas = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.M365ChatCanvas;
};

export type QueryAnnotationImageFile = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.ImageFile;
};

export type QueryAnnotationApp = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.App;
};

export type QueryAnnotationPowerApps = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.PowerApps;
};

export type QueryAnnotationCopilotSearch = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.CopilotSearch;
};

export type QueryAnnotationEmailConversation = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.EmailConversation;
};

export type QueryAnnotationSchedulingEventIntent = QueryAnnotation & {
  readonly queryAnnotationType: QueryAnnotationType.SchedulingEventIntent;
};
